import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getScanRecommendations } from '../../services/BeholderService';

const OUTLOOK_COLORS = {
    BULLISH: 'success',
    BEARISH: 'danger',
    NEUTRAL: 'secondary'
};

const OUTLOOK_LABELS = {
    BULLISH: 'Mercado em Alta',
    BEARISH: 'Mercado em Baixa',
    NEUTRAL: 'Mercado Neutro'
};

function timeAgo(timestamp) {
    if (!timestamp) return null;
    const diffMs = Date.now() - timestamp;
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1) return 'agora há pouco';
    if (diffMin < 60) return `há ${diffMin} min`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `há ${diffH} h`;
    return `há ${Math.round(diffH / 24)} dias`;
}

/**
 * Pulso do Mercado — widget sempre visível no Dashboard mostrando a
 * análise mais recente do scanner de mercado da IA.
 *
 * props:
 * - refreshTick:   incrementar para forçar reload (ex: ao receber WS)
 * - aiAutoSwitch:  bool — se true, mostra indicador "Auto ON" (bot pode
 *                  rotacionar). Se false/undefined, mostra "Auto OFF"
 *                  (sugestões informativas apenas).
 * - aiProvider:    string — se vazio, mostra empty state pedindo setup.
 */
function ScanRecommendationsPanel({ refreshTick, aiAutoSwitch, aiProvider }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    function load() {
        setLoading(true);
        getScanRecommendations()
            .then(result => { setData(result); setLoading(false); })
            .catch(() => { setData(null); setLoading(false); });
    }

    useEffect(() => { load(); }, [refreshTick]);

    // Auto-refresh a cada 2 min — fallback para quando a notificação WS não chega
    useEffect(() => {
        const id = setInterval(load, 2 * 60 * 1000);
        return () => clearInterval(id);
    }, []);

    // ── Empty state 1: usuário não configurou provedor de IA ─────────────
    if (aiProvider !== undefined && !aiProvider) {
        return (
            <div className="card card-body border-0 shadow mb-4">
                <h2 className="fs-5 fw-bold mb-2">🔮 Pulso do Mercado</h2>
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                    Configure um provedor de IA em <Link to="/settings">Settings</Link> para
                    ver a análise de mercado e os melhores ativos do momento.
                </p>
            </div>
        );
    }

    if (loading) return null;

    // ── Empty state 2: AI configurada mas nenhum scan rodou ainda ───────
    if (!data || !data.focusSymbols) {
        return (
            <div className="card card-body border-0 shadow mb-4">
                <h2 className="fs-5 fw-bold mb-2">🔮 Pulso do Mercado</h2>
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                    Aguardando primeira análise do scanner.
                    Ative uma estratégia <strong>AI Full</strong> em <Link to="/strategies">Estratégias</Link> para
                    começar a coletar sinais.
                </p>
            </div>
        );
    }

    const outlookColor = OUTLOOK_COLORS[data.marketOutlook] || 'secondary';
    const outlookLabel = OUTLOOK_LABELS[data.marketOutlook] || data.marketOutlook;
    const scannedAtAbs = data._timestamp ? new Date(data._timestamp).toLocaleTimeString() : null;
    const scannedAtRel = timeAgo(data._timestamp);

    const autoBadge = aiAutoSwitch
        ? { cls: 'bg-success', icon: '⚡', label: 'Auto ON', title: 'Troca automática ativa — bot pode rotacionar símbolos conforme a IA recomendar' }
        : { cls: 'bg-secondary', icon: '👀', label: 'Auto OFF', title: 'Troca automática desativada — sugestões da IA são apenas informativas' };

    const hasFocus = data.focusSymbols && data.focusSymbols.length > 0;
    const hasCandidates = data.addCandidates && data.addCandidates.length > 0;
    const hasDrops = data.dropSymbols && data.dropSymbols.length > 0;

    return (
        <div className="card card-body border-0 shadow mb-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="d-flex align-items-center gap-2">
                    <h2 className="fs-5 fw-bold mb-0">🔮 Pulso do Mercado</h2>
                    <span className={`badge bg-${outlookColor}`} title={data.reasoning || ''}>
                        {outlookLabel}
                    </span>
                    <span className={`badge ${autoBadge.cls}`} title={autoBadge.title}>
                        {autoBadge.icon} {autoBadge.label}
                    </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                    {scannedAtRel && (
                        <small className="text-muted" title={scannedAtAbs ? `Última análise às ${scannedAtAbs}` : ''}>
                            {scannedAtRel}
                        </small>
                    )}
                    <button
                        className="btn btn-sm btn-link py-0 text-secondary"
                        onClick={load}
                        title="Recarregar dados do scanner">↻</button>
                </div>
            </div>

            {/* Três blocos */}
            <div className="row g-3">
                <div className="col-md-4">
                    <div className="text-uppercase fw-bold text-muted mb-1" style={{ fontSize: 11, letterSpacing: 0.5 }}>
                        🔥 Em Alta
                    </div>
                    {hasFocus ? (
                        <div className="d-flex gap-1 flex-wrap">
                            {data.focusSymbols.map(s => (
                                <span key={s} className="badge bg-success fs-6 px-3 py-2" title={data.reasoning || ''}>
                                    {s}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <span className="text-muted" style={{ fontSize: 13 }}>—</span>
                    )}
                </div>

                <div className="col-md-4">
                    <div className="text-uppercase fw-bold text-muted mb-1" style={{ fontSize: 11, letterSpacing: 0.5 }}>
                        🌱 Novos Candidatos
                    </div>
                    {hasCandidates ? (
                        <div className="d-flex gap-1 flex-wrap">
                            {data.addCandidates.map(s => (
                                <span key={s} className="badge bg-info text-dark fs-6 px-3 py-2" title={data.reasoning || ''}>
                                    {s}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <span className="text-muted" style={{ fontSize: 13 }}>—</span>
                    )}
                </div>

                <div className="col-md-4">
                    <div className="text-uppercase fw-bold text-muted mb-1" style={{ fontSize: 11, letterSpacing: 0.5 }}>
                        📉 Saindo do Foco
                    </div>
                    {hasDrops ? (
                        <div className="d-flex gap-1 flex-wrap">
                            {data.dropSymbols.map(s => (
                                <span key={s} className="badge bg-secondary fs-6 px-3 py-2" title={data.reasoning || ''}>
                                    {s}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <span className="text-muted" style={{ fontSize: 13 }}>—</span>
                    )}
                </div>
            </div>

            {/* Reasoning agregado */}
            {data.reasoning && (
                <div className="mt-3 pt-2 border-top">
                    <small className="text-muted fst-italic">
                        💬 {data.reasoning}
                    </small>
                </div>
            )}
        </div>
    );
}

export default ScanRecommendationsPanel;
